import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  CalendarClock, Clock, HeartPulse, IndianRupee, Loader2, RefreshCw, Stethoscope,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import CollectPaymentPanel from './CollectPaymentPanel';
import TriageDrawer from '../TriageDrawer';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const PAY_TONE = {
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  waived: 'bg-sky-50 text-sky-700 border-sky-200',
  unpaid: 'bg-amber-50 text-amber-800 border-amber-200',
};

/** Reception board: triage pending → collect fee if any → awaiting consultation. */
export default function ReceptionAppointmentsBoard({ brand = '#0f766e' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [payFor, setPayFor] = useState(null);
  const [triageFor, setTriageFor] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await ax.get('/api/clinic-ops/todays-appointments');
      setData(d);
    } catch (_) {
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
  }, [reload]);

  const rows = (data?.appointments || []).filter((a) => {
    if (filter === 'triage') return a.needs_triage;
    if (filter === 'unpaid') return a.needs_payment;
    if (filter === 'awaiting') return a.status === 'awaiting_consultation';
    return true;
  });

  return (
    <div className="rounded-2xl border bg-white overflow-hidden shadow-sm" data-testid="reception-appointments-board">
      <div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-3"
        style={{ background: `linear-gradient(90deg, ${brand}12, transparent)` }}>
        <div>
          <h3 className="font-bold text-zinc-900 text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <CalendarClock className="h-4 w-4" style={{ color: brand }} />
            Today&apos;s appointments
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            {data?.date || '—'} · {data?.counts?.total ?? 0} total
            · {data?.counts?.triage_pending ?? 0} triage pending
            · {data?.counts?.unpaid ?? 0} payment due
            · {data?.counts?.awaiting ?? 0} awaiting doctor
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {['all', 'triage', 'unpaid', 'awaiting'].map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)}
              className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${
                filter === f ? 'text-white border-transparent' : 'text-zinc-500 border-zinc-200'
              }`}
              style={filter === f ? { background: brand } : {}}
              data-testid={`appt-filter-${f}`}>
              {f}
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={reload} disabled={loading} className="h-7">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
      </div>

      {payFor && (
        <div className="px-5 pt-4">
          <CollectPaymentPanel
            appointment={payFor}
            onDone={() => { setPayFor(null); reload(); }}
            onCancel={() => setPayFor(null)}
          />
        </div>
      )}

      <div className="divide-y max-h-[520px] overflow-y-auto">
        {loading && !rows.length ? (
          <div className="py-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-400">No appointments for this filter.</div>
        ) : rows.map((a) => (
          <AppointmentRow
            key={`${a.kind}-${a.id}`}
            a={a}
            brand={brand}
            onCollect={() => setPayFor(a)}
            onTriage={() => setTriageFor(a)}
            collecting={payFor?.id === a.id}
          />
        ))}
      </div>

      <TriageDrawer
        open={Boolean(triageFor)}
        queueEntry={triageFor}
        onClose={() => setTriageFor(null)}
        onSaved={() => { setTriageFor(null); reload(); }}
      />
    </div>
  );
}

function AppointmentRow({ a, brand, onCollect, onTriage, collecting }) {
  const pay = a.payment_status || 'unpaid';
  return (
    <div className={`px-5 py-3.5 flex flex-wrap gap-3 items-start hover:bg-zinc-50/80 ${collecting ? 'bg-amber-50/40' : ''}`}
      data-testid={`appt-row-${a.id}`}>
      <div className="h-10 w-10 rounded-xl flex items-center justify-center text-xs font-extrabold text-white flex-shrink-0"
        style={{ background: brand }}>
        {a.queue_no || '—'}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-sm text-zinc-900 truncate">{a.patient_name}</p>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-800">
            {a.status_label || a.status || '—'}
          </span>
          <span className={`text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${PAY_TONE[pay] || PAY_TONE.unpaid}`}>
            {pay}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1"><Stethoscope className="h-3 w-3" />{a.doctor_name}</span>
          {a.slot && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{a.slot}</span>}
          {a.patient_number && (
            <span className="font-mono text-[#F15A22] font-semibold">
              <span className="text-[9px] uppercase tracking-wider mr-1 opacity-80">Patient</span>
              {a.patient_number}
            </span>
          )}
          {a.appointment_number && (
            <span className="font-mono text-[#071B34] font-semibold">
              <span className="text-[9px] uppercase tracking-wider mr-1 text-zinc-400">Appt</span>
              {a.appointment_number}
            </span>
          )}
          <span className="inline-flex items-center gap-1"><IndianRupee className="h-3 w-3" />{fmt(a.suggested_fee)}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {a.kind === 'queue' && a.needs_triage && (
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={onTriage}
            data-testid={`appt-triage-${a.id}`}>
            <HeartPulse className="h-3 w-3" /> Triage
          </Button>
        )}
        {a.needs_payment && a.kind === 'queue' && (
          <Button size="sm" className="h-8 text-xs" style={{ background: brand }} onClick={onCollect}
            data-testid={`appt-collect-${a.id}`}>
            Collect payment
          </Button>
        )}
      </div>
    </div>
  );
}
